import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#E24B4A', bg: '#F4F6F9' };
const SEV = { critical: C.red, high: C.amber, medium: C.blue };
const ts = (s) => (s ? `${s.slice(0, 16).replace('T', ' ')}Z` : '—');

function Kpi({ label, value, color, testid }) {
  return (
    <div className="rounded-sm px-3 py-2" style={{ background: C.bg }} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide" style={{ color: C.slate }}>{label}</p>
      <p className="text-sm font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </div>
  );
}

function RuleRow({ r }) {
  return (
    <tr className="border-t" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid={`invariant-rule-${r.rule_id}`}>
      <td className="py-1 font-mono text-[10px]" style={{ color: C.slate }}>{r.rule_id}</td>
      <td className="py-1 whitespace-normal" style={{ color: C.navy }}>{r.title}</td>
      <td className="py-1" style={{ color: C.slate }}>{r.owner}</td>
      <td className="py-1"><span className="px-1.5 rounded-sm text-[9px] font-semibold text-white" style={{ background: SEV[r.severity] }}>{r.severity}</span></td>
      <td className="py-1 text-right font-semibold" style={{ color: r.count ? SEV[r.severity] : C.green }} data-testid={`invariant-count-${r.rule_id}`}>{r.skipped ? '—' : r.count}</td>
    </tr>
  );
}

export default function InvariantsCard({ api }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const canRun = ['platform_owner', 'mga_admin'].includes(user?.role);

  const load = useCallback(() => api.get('/invariants/latest').then(r => { setData(r.data); setFailed(false); }).catch(() => setFailed(true)), [api]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try { const r = await api.post('/invariants/run'); toast.success(`Invariants evaluated — gate ${r.data.gate}, ${r.data.violations} breach(es)`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Invariant run failed'); }
    finally { setBusy(false); }
  };

  const latest = data?.latest;
  const caught = data?.caught;
  const rules = latest?.rules || [];
  const failing = rules.filter(r => r.count);
  const shown = showAll ? rules : failing;
  const gateOk = latest?.gate === 'clear';

  return (
    <div className="bg-white border rounded-sm p-4" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="invariants-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {gateOk ? <ShieldCheck className="w-4 h-4" style={{ color: C.green }} /> : <ShieldAlert className="w-4 h-4" style={{ color: C.red }} />}
          <div>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Platform invariants — {rules.length || 26} rules the book must hold</h3>
            <p className="text-[11px] whitespace-normal" style={{ color: C.slate }}>
              {latest ? `Last evaluated ${ts(latest.evaluated_at)} (${latest.source || 'nightly'}) · gate ${latest.gate}` : failed ? 'Could not load the invariant run' : 'No run yet — evaluate now'}
            </p>
          </div>
        </div>
        {canRun && <button onClick={run} disabled={busy} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm text-white disabled:opacity-60" style={{ background: C.blue }} data-testid="invariants-run-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Evaluate now</button>}
      </div>

      {latest && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
          <Kpi label="gate" value={latest.gate} color={gateOk ? C.green : C.red} testid="invariants-gate" />
          <Kpi label="critical" value={latest.by_severity?.critical ?? 0} color={latest.by_severity?.critical ? C.red : C.navy} testid="invariants-critical" />
          <Kpi label="high" value={latest.by_severity?.high ?? 0} color={latest.by_severity?.high ? C.amber : C.navy} testid="invariants-high" />
          <Kpi label="medium" value={latest.by_severity?.medium ?? 0} testid="invariants-medium" />
          <Kpi label="caught / resolved · 7d" value={caught ? `${caught.caught} / ${caught.resolved} · ${caught.still_open} open` : '—'} testid="invariants-caught" />
        </div>
      )}

      {latest && (
        <>
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px]" style={{ color: C.slate }}>{failing.length ? `${failing.length} rule(s) in breach` : 'Every rule holds on this book'}</p>
            <button onClick={() => setShowAll(v => !v)} className="text-[11px] underline" style={{ color: C.blue }} data-testid="invariants-toggle-all">{showAll ? 'Show breaches only' : 'Show all rules'}</button>
          </div>
          {shown.length > 0 && (
            <table className="w-full mt-1 text-[11px]" data-testid="invariants-rules-table">
              <thead><tr style={{ color: C.slate }}><th className="text-left py-1 w-12">rule</th><th className="text-left">assertion</th><th className="text-left w-24">owner</th><th className="text-left w-16">severity</th><th className="text-right w-16">in breach</th></tr></thead>
              <tbody>{shown.map(r => <RuleRow key={r.rule_id} r={r} />)}</tbody>
            </table>
          )}
          <p className="text-[10px] mt-2 whitespace-normal" style={{ color: C.slate }}>{latest.basis}</p>
        </>
      )}
    </div>
  );
}
